import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Plus, Edit, Filter, MapPin, Phone, Mail, Building } from 'lucide-react';
import { Stakeholder } from '@/types/materiality';
import { materialityService } from '@/utils/materialityService';

interface StakeholderMappingProps {}

export const StakeholderMapping: React.FC<StakeholderMappingProps> = () => {
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStakeholder, setSelectedStakeholder] = useState<Stakeholder | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching stakeholders...');
        const stakeholdersData = await materialityService.getStakeholders();
        console.log('Received stakeholders:', stakeholdersData);
        setStakeholders(stakeholdersData);
      } catch (error) {
        console.error('Error fetching stakeholders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredStakeholders = stakeholders.filter(stakeholder => {
    if (filterType !== 'all' && stakeholder.type !== filterType) return false;
    if (filterCategory !== 'all' && stakeholder.category !== filterCategory) return false;
    if (filterPriority !== 'all' && stakeholder.priority !== filterPriority) return false;
    return true;
  });

  const getInfluenceColor = (influence: string) => {
    switch (influence) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInterestColor = (interest: string) => {
    switch (interest) {
      case 'high': return 'bg-blue-100 text-blue-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'passive': return 'bg-yellow-100 text-yellow-800';
      case 'resistant': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'important': return 'bg-orange-100 text-orange-800';
      case 'monitor': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Stakeholder Mapping
            </CardTitle>
            <CardDescription>
              Manage and track stakeholder relationships and engagement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading stakeholder data...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Stakeholder Mapping
              </CardTitle>
              <CardDescription>
                Manage and track stakeholder relationships and engagement
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Stakeholder
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="external">External</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="investors">Investors</SelectItem>
                <SelectItem value="customers">Customers</SelectItem>
                <SelectItem value="employees">Employees</SelectItem>
                <SelectItem value="suppliers">Suppliers</SelectItem>
                <SelectItem value="regulators">Regulators</SelectItem>
                <SelectItem value="communities">Communities</SelectItem>
                <SelectItem value="ngos">NGOs</SelectItem>
                <SelectItem value="media">Media</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="important">Important</SelectItem>
                <SelectItem value="monitor">Monitor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Influence</TableHead>
                  <TableHead>Interest</TableHead>
                  <TableHead>Engagement</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Next Contact</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStakeholders.map((stakeholder) => (
                  <TableRow key={stakeholder.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{stakeholder.name}</div>
                        <div className="text-sm text-gray-500">{stakeholder.organization}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={stakeholder.type === 'internal' ? 'default' : 'secondary'}>
                        {stakeholder.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{stakeholder.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getInfluenceColor(stakeholder.influence)}>
                        {stakeholder.influence}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getInterestColor(stakeholder.interest)}>
                        {stakeholder.interest}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getEngagementColor(stakeholder.engagementLevel)}>
                        {stakeholder.engagementLevel}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPriorityColor(stakeholder.priority)}>
                        {stakeholder.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {new Date(stakeholder.nextContact).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedStakeholder(stakeholder);
                          setIsEditDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredStakeholders.length === 0 && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No stakeholders found matching the current filters.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stakeholder Detail Dialog */}
      <Dialog open={!!selectedStakeholder && !isEditDialogOpen} onOpenChange={() => setSelectedStakeholder(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedStakeholder?.name}
            </DialogTitle>
            <DialogDescription>
              Detailed stakeholder information and contact details
            </DialogDescription>
          </DialogHeader>
          {selectedStakeholder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Organization</label>
                  <p className="text-sm flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    {selectedStakeholder.organization}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Category</label>
                  <Badge variant="outline">{selectedStakeholder.category}</Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Contact Person</label>
                  <p className="text-sm">{selectedStakeholder.contactPerson}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedStakeholder.email}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="text-sm flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {selectedStakeholder.phone}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Contact</label>
                  <p className="text-sm">{new Date(selectedStakeholder.lastContact).toLocaleDateString()}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Notes</label>
                <p className="text-sm mt-1">{selectedStakeholder.notes}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Material Topics</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedStakeholder.materialTopics.map((topic, index) => (
                    <Badge key={index} variant="secondary">{topic}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Stakeholder Dialog - Placeholder for future implementation */}
      <Dialog open={isAddDialogOpen || isEditDialogOpen} onOpenChange={() => {
        setIsAddDialogOpen(false);
        setIsEditDialogOpen(false);
        setSelectedStakeholder(null);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isAddDialogOpen ? 'Add New Stakeholder' : 'Edit Stakeholder'}
            </DialogTitle>
            <DialogDescription>
              {isAddDialogOpen ? 'Add a new stakeholder to your mapping' : 'Update stakeholder information'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Stakeholder management functionality will be implemented in a future update.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 